import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Clock, TrendingUp } from "lucide-react";

export default function FeatureAdDialog({ open, onOpenChange, adId }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/plans").then((r) => {
      const isBusiness = user?.account_type === "empresa";
      const filtered = r.data.filter(
        (p) => p.kind === "featured" && p.audience === (isBusiness ? "empresa" : "particular"),
      );
      setPlans(filtered);
      if (filtered[0]) setSelected(filtered[0].lookup_key);
    });
  }, [open, user]);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
        ad_id: adId,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="feature-ad-dialog"
        className="max-w-lg rounded-none border border-[#E2E8F0] bg-[#F8FAFC] p-8"
      >
        <DialogHeader className="text-left space-y-1">
          <p className="eyebrow flex items-center gap-1"><Sparkles className="w-3 h-3" /> Destacar anuncio</p>
          <DialogTitle className="font-serif text-3xl font-semibold text-[#1A1A1A]">
            Máxima visibilidad, sin esfuerzo.
          </DialogTitle>
          <DialogDescription className="text-[#475569] text-sm">
            Los anuncios destacados aparecen los primeros en tu categoría y ciudad, con un
            distintivo dorado. Perfecto para cerrar rápido.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {plans.map((p) => (
            <button
              key={p.lookup_key}
              type="button"
              onClick={() => setSelected(p.lookup_key)}
              data-testid={`feature-plan-${p.lookup_key}`}
              className={`w-full text-left border p-4 flex items-center justify-between transition-colors ${
                selected === p.lookup_key
                  ? "border-[#3B5FB8] bg-white"
                  : "border-[#E2E8F0] bg-white hover:border-[#3B5FB8]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#EEF2F7] flex items-center justify-center">
                  {p.interval === "semana" ? <Clock className="w-4 h-4 text-[#3B5FB8]" /> : <TrendingUp className="w-4 h-4 text-[#3B5FB8]" />}
                </div>
                <div>
                  <p className="font-serif text-lg font-semibold text-[#1A1A1A]">{p.label}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#3B5FB8]">
                    Duración: {p.interval === "semana" ? "7 días" : "30 días"}
                  </p>
                </div>
              </div>
              <p className="font-serif text-2xl font-semibold text-[#1A1A1A]">
                {p.amount.toFixed(2).replace(".", ",")} €
              </p>
            </button>
          ))}
        </div>

        <Button
          data-testid="feature-checkout"
          onClick={submit}
          disabled={!selected || busy}
          className="mt-2 h-12 rounded-none bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] font-medium"
        >
          {busy ? "Redirigiendo a Stripe..." : "Pagar y destacar"}
        </Button>
        <p className="text-xs text-[#64748B] text-center">
          Pago único seguro con Stripe. Puedes cancelar antes de pagar sin coste.
        </p>
      </DialogContent>
    </Dialog>
  );
}
